import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangeEvent, FocusEvent, ReactNode } from "react";
import { Link, ScrollRestoration, useNavigate } from "react-router-dom";
import { ErrorMessage, Form, Formik } from "formik";
import * as yup from "yup";
import { HashLoader } from "react-spinners";
import { PhoneCall } from "@phosphor-icons/react";
import { getCountryCallingCode } from "react-phone-number-input";
import SignUpNavbar from "../../components/SignUpNavbar";
import BasePhoneInput from "../../components/BasePhoneInput";
import OTPInput from "../../components/OTPInput";
import { useAuth } from "../../context/AuthContext";
import useSendContactPhoneOtp from "../../hooks/useSendContactPhoneOtp";
import useVerifyContactPhoneOtp from "../../hooks/useVerifyContactPhoneOtp";
import useResolveGeo from "../../hooks/useResolveGeo";
import {
  defaultPhoneCodeFor,
  normalizePhoneCode,
  phoneInputCountry,
  splitPhoneNumber,
} from "../../utils/phoneNumber";

const schema = yup.object().shape({
  phone: yup.string().required("Phone number is required"),
  smsConsent: yup
    .boolean()
    .oneOf([true], "Please agree to receive SMS at this number to continue")
    .required("Please agree to receive SMS at this number to continue"),
  termsConsent: yup
    .boolean()
    .oneOf([true], "Please agree to the Privacy Policy and Terms & Conditions to continue")
    .required("Please agree to the Privacy Policy and Terms & Conditions to continue"),
});

const legalLinkClass = "text-visaro-orange underline underline-offset-2";

const TermsLink = () => (
  <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" className={legalLinkClass}>
    Terms &amp; Conditions
  </Link>
);

const PrivacyLink = () => (
  <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" className={legalLinkClass}>
    Privacy Policy
  </Link>
);

/** Required consent checkbox with its own error, matching Termii's opt-in sample. */
const ConsentCheckbox = ({
  name,
  checked,
  error,
  onChange,
  onBlur,
  children,
}: {
  name: string;
  checked: boolean;
  error?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        id={name}
        name={name}
        checked={checked}
        onChange={onChange}
        onBlur={onBlur}
        required
        aria-describedby={error ? `${name}-error` : undefined}
        aria-invalid={error ? true : undefined}
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded accent-deepBlue focus:outline-none focus-visible:ring-2 focus-visible:ring-deepBlue/30 focus-visible:ring-offset-2"
      />
      <label htmlFor={name} className="text-sm leading-relaxed text-gray-600 text-left cursor-pointer">
        {children}
      </label>
    </div>
    {error && (
      <div id={`${name}-error`} role="alert" className="text-red-500 text-left text-sm pl-7">
        {error}
      </div>
    )}
  </div>
);

const ContactPhoneVerification = () => {
  const navigate = useNavigate();
  const { user, refetch } = useAuth();

  const [step, setStep] = useState<"entry" | "otp">("entry");
  const [submittedPhone, setSubmittedPhone] = useState("");
  // Held alongside the number so a resend goes to the country the customer
  // actually picked, not to a hardcoded default.
  const [submittedPhoneCode, setSubmittedPhoneCode] = useState("+234");
  const [otp, setOTP] = useState("");
  const [timer, setTimer] = useState(60);
  const [resendEnabled, setResendEnabled] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    // Already verified — don't make the user redo it.
    if (user?.contact_phone_verified) {
      navigate("/sign-up/confirm-residence", { replace: true });
    }
  }, [user, navigate]);

  const { sendContactOTP, sendingContactOTP, isSendError, sendError } =
    useSendContactPhoneOtp({
      onSuccess() {
        setStep("otp");
        setTimer(60);
        setResendEnabled(false);
      },
    });

  const { validateContactOTP, validatingContactOTP, isContactOTPError, contactOtpError } =
    useVerifyContactPhoneOtp({
      async onSuccess() {
        await refetch();
        navigate("/sign-up/confirm-residence");
      },
    });

  const isOtpValid = useMemo(() => {
    return (
      otp.length === 4 &&
      otp.split("").every((v) => v !== "" && !isNaN(Number(v)))
    );
  }, [otp]);

  // Opens the country picker where the customer actually is, rather than making
  // someone in Berlin scroll past Nigeria. Detection failing is fine — `country`
  // is null and phoneInputCountry falls back to NG.
  const { country } = useResolveGeo();
  const defaultPhoneCode = useMemo(
    () => defaultPhoneCodeFor(user?.phone_code, country),
    [user?.phone_code, country]
  );

  const isLoading = useMemo(
    () => sendingContactOTP || validatingContactOTP,
    [sendingContactOTP, validatingContactOTP]
  );

  const isError = useMemo(
    () => isSendError || isContactOTPError,
    [isSendError, isContactOTPError]
  );

  const error = useMemo(
    () => sendError || contactOtpError,
    [sendError, contactOtpError]
  );

  const handleResend = useCallback(() => {
    if (!submittedPhone) return;
    // Previously hardcoded to +234, which sent the resent code to the wrong
    // country for anyone who had picked a different one on the entry screen.
    sendContactOTP({
      phone_code: submittedPhoneCode,
      phone: submittedPhone,
    });
  }, [sendContactOTP, submittedPhone, submittedPhoneCode]);

  const handleVerify = () => {
    validateContactOTP({ code: otp });
  };

  useEffect(() => {
    if (step !== "otp") return;
    let interval: any;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      clearInterval(interval);
      setResendEnabled(true);
    }
    return () => clearInterval(interval);
  }, [timer, step]);

  return (
    <div>
      <ScrollRestoration />
      {isLoading && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <SignUpNavbar />

      {step === "entry" ? (
        <Formik
          initialValues={{
            phone: "",
            phone_code: defaultPhoneCode,
            smsConsent: false,
            termsConsent: false,
          }}
          enableReinitialize
          validationSchema={schema}
          onSubmit={(values) => {
            if (!values.smsConsent || !values.termsConsent) return;
            const { phone_code, phone } = splitPhoneNumber(
              values.phone,
              values.phone_code
            );
            setSubmittedPhone(phone);
            setSubmittedPhoneCode(phone_code);
            sendContactOTP({ phone_code, phone });
          }}
        >
          {({
            isValid,
            dirty,
            values,
            errors,
            touched,
            setFieldValue,
            handleChange,
            handleBlur,
          }) => (
            <Form className="flex flex-col justify-center items-center p-10 px-5 md:px-20 gap-8 text-lg">
              <div className="flex flex-col justify-center items-center gap-3">
                <div className="text-3xl md:text-3xl lg:text-5xl max-w-3xl font-bold text-center text-deepBlue">
                  What&apos;s the best number to reach you on?
                </div>
                <div className="text-gray-600 text-center max-w-lg space-y-2">
                  <p>
                    We&apos;ll send a code to confirm it. Our support team uses
                    this number to reach you, so please use a phone number you
                    actually use.
                  </p>
                </div>
              </div>

              <div className="w-full flex-col gap-5 flex items-center justify-center text-base">
                <div className="flex flex-col gap-2 max-w-md w-full">
                  <label
                    htmlFor="phone"
                    className="text-sm font-bold text-gray-700"
                  >
                    Contact Phone Number*
                  </label>
                  <BasePhoneInput
                    placeholder="Enter your phone number"
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry={phoneInputCountry(country)}
                    value={values.phone}
                    onChange={(value: string | undefined) => {
                      setFieldValue("phone", value);
                    }}
                    onCountryChange={(country: any) => {
                      if (country) {
                        const code = getCountryCallingCode(country);
                        setFieldValue("phone_code", normalizePhoneCode(code));
                      }
                    }}
                    onBlur={handleBlur("phone")}
                    name="phone"
                    id="phone"
                    className="flex h-12 w-full items-center rounded-lg border border-gray-300 bg-white px-3 text-base text-deepBlue shadow-sm transition-colors hover:border-gray-400 focus-within:border-deepBlue focus-within:ring-2 focus-within:ring-deepBlue/10 focus-within:hover:border-deepBlue"
                  />
                  <ErrorMessage
                    name="phone"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                <div className="flex flex-col gap-4 max-w-md w-full">
                  <ConsentCheckbox
                    name="smsConsent"
                    checked={values.smsConsent}
                    error={touched.smsConsent ? errors.smsConsent : undefined}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    By submitting your number, you consent to receive SMS
                    messages from Visaro for verification codes and two-factor
                    authentication (2FA) only. Message and data rates may
                    apply. See our{" "}
                    <TermsLink /> and <PrivacyLink />.
                  </ConsentCheckbox>
                  <ConsentCheckbox
                    name="termsConsent"
                    checked={values.termsConsent}
                    error={touched.termsConsent ? errors.termsConsent : undefined}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    I have read and agree to Visaro&apos;s <PrivacyLink /> and{" "}
                    <TermsLink />.
                  </ConsentCheckbox>
                </div>

                <div className="flex flex-col gap-2 max-w-md w-full">
                  {isError && (
                    <div className="text-red-500 text-left text-sm">
                      {error?.message}
                    </div>
                  )}
                  <button
                    disabled={
                      !isValid ||
                      !dirty ||
                      !values.smsConsent ||
                      !values.termsConsent ||
                      isLoading
                    }
                    type="submit"
                    className={`p-4 px-5 w-full border rounded-lg font-bold text-white text-sm ${
                      isValid &&
                      dirty &&
                      values.smsConsent &&
                      values.termsConsent &&
                      !isLoading
                        ? "bg-deepBlue hover:bg-blue-700"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    {isLoading ? "Sending..." : "Send Code"}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="flex flex-col justify-center items-center p-20 px-5 md:px-20 gap-8 text-lg">
          <div className="p-5 bg-gray-100 rounded-full">
            <PhoneCall size={30} />
          </div>
          <div>
            <div className="font-medium text-center max-w-lg">
              Please check your SMS or WhatsApp.
            </div>
            <div className="text-gray-500 text-center max-w-lg">
              {submittedPhone
                ? `We've sent a code to ${submittedPhone}`
                : "We've sent a code to your phone number"}
            </div>
          </div>
          <div className="w-full flex-col gap-5 flex items-center justify-center text-base">
            <OTPInput onChange={(value) => setOTP(value)} length={4} />
            {!resendEnabled && (
              <div className="text-gray-500">Resend in {timer} seconds</div>
            )}
            {resendEnabled && (
              <div className="text-gray-500">
                Didn&apos;t get a code?{" "}
                <span
                  className="underline"
                  onClick={handleResend}
                  style={{ cursor: "pointer" }}
                >
                  Click to resend.
                </span>
              </div>
            )}
            <div className="flex flex-col gap-2 max-w-md w-full items-center">
              {isError && (
                <div className="text-red-500 text-center w-full">
                  {error?.message}
                </div>
              )}
              <button
                disabled={!isOtpValid}
                onClick={handleVerify}
                className={`p-4 px-5 w-full border rounded-lg font-bold text-white text-sm ${
                  isOtpValid
                    ? "bg-deepBlue hover:bg-blue-700"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={() => setStep("entry")}
                className="text-center text-gray-600 text-sm"
              >
                Wrong number?{" "}
                <span className="text-visaro-orange">Change it</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactPhoneVerification;
